import React, { useEffect, useState } from "react";
import { ChevronDown, BookOpen, Sparkles, LifeBuoy, Inbox, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/utils";

const CATEGORIES = [
  { key: "account", label: "Account & login" },
  { key: "payments", label: "Payments" },
  { key: "developer", label: "About a developer" },
  { key: "technical", label: "Technical / bug" },
  { key: "other", label: "Other" },
];

const STATUS_STYLES = {
  open: "bg-sky-50 text-sky-700 border-sky-200",
  in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function StatusPill({ status }) {
  const label = { open: "Open", in_progress: "In Progress", resolved: "Resolved" }[status] || status;
  return <span data-testid={`ticket-status-${status}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.open}`}>{label}</span>;
}

export default function Help() {
  const [tab, setTab] = useState("faqs");
  const [faqs, setFaqs] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([api.get("/help/faqs"), api.get("/help/glossary")]);
      setFaqs(a.data);
      setGlossary(b.data);
    })();
  }, []);

  const grouped = faqs.reduce((m, f) => {
    (m[f.category] = m[f.category] || []).push(f);
    return m;
  }, {});

  return (
    <div className="max-w-4xl mx-auto">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Help Center</p>
      <h1 className="text-4xl font-bold text-neutral-900 mt-2">FAQs, glossary &amp; support</h1>
      <p className="text-neutral-500 mt-2">Everything you need to invest with confidence — and a direct line to us when you get stuck.</p>

      <div className="flex gap-1 border-b border-neutral-200 mt-8 overflow-x-auto no-scrollbar">
        <Tab active={tab === "faqs"} onClick={() => setTab("faqs")} testId="tab-faqs"><Sparkles size={14} className="inline mr-2 -mt-1" />FAQs</Tab>
        <Tab active={tab === "glossary"} onClick={() => setTab("glossary")} testId="tab-glossary"><BookOpen size={14} className="inline mr-2 -mt-1" />Glossary</Tab>
        <Tab active={tab === "contact"} onClick={() => setTab("contact")} testId="tab-contact"><LifeBuoy size={14} className="inline mr-2 -mt-1" />Contact Support</Tab>
        <Tab active={tab === "requests"} onClick={() => setTab("requests")} testId="tab-requests"><Inbox size={14} className="inline mr-2 -mt-1" />My Requests</Tab>
      </div>

      {tab === "faqs" && (
        <div className="mt-8 space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-xs uppercase tracking-[0.18em] text-propera font-semibold mb-3">{cat}</p>
              <div className="space-y-2">
                {items.map((q, i) => {
                  const key = `${cat}-${i}`;
                  const open = openIdx === key;
                  return (
                    <div key={key} className="soft-card overflow-hidden">
                      <button
                        data-testid={`faq-${cat}-${i}`}
                        onClick={() => setOpenIdx(open ? null : key)}
                        className="w-full flex items-center justify-between p-5 text-left"
                      >
                        <span className="font-semibold text-neutral-900">{q.q}</span>
                        <ChevronDown size={18} className={`transition ${open ? "rotate-180 text-propera" : "text-neutral-400"}`} />
                      </button>
                      {open && <div className="px-5 pb-5 text-sm text-neutral-600 leading-relaxed">{q.a}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "glossary" && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {glossary.map((g, i) => (
            <div key={i} className="soft-card p-5">
              <p className="font-semibold text-neutral-900">{g.term}</p>
              <p className="text-sm text-neutral-600 mt-1">{g.def}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "contact" && <ContactForm onSent={() => setTab("requests")} />}
      {tab === "requests" && <MyRequests />}

      <div className="mt-16 pt-8 border-t border-neutral-200">
        <p className="text-xs uppercase tracking-[0.18em] text-propera font-semibold mb-3">Legal</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" data-testid="help-privacy" className="soft-card p-5 soft-card-hover">
            <p className="font-semibold text-neutral-900">Privacy Policy</p>
            <p className="text-xs text-neutral-500 mt-1">How we collect &amp; protect your personal data (UK / EU GDPR).</p>
          </a>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" data-testid="help-terms" className="soft-card p-5 soft-card-hover">
            <p className="font-semibold text-neutral-900">Terms of Service</p>
            <p className="text-xs text-neutral-500 mt-1">The agreement between you and Propera.</p>
          </a>
          <a href="/cookies.html" target="_blank" rel="noopener noreferrer" data-testid="help-cookies" className="soft-card p-5 soft-card-hover">
            <p className="font-semibold text-neutral-900">Cookie Policy</p>
            <p className="text-xs text-neutral-500 mt-1">Which cookies we use &amp; how to manage them.</p>
          </a>
        </div>
      </div>
    </div>
  );
}

function ContactForm({ onSent }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (subject.trim().length < 3) { toast.error("Subject too short"); return; }
    if (body.trim().length < 10) { toast.error("Please describe your issue in more detail"); return; }
    setSubmitting(true);
    try {
      await api.post("/support/tickets", { subject: subject.trim(), body: body.trim(), category });
      toast.success("Support request sent — we'll be in touch soon.");
      setSubject(""); setBody(""); setCategory("other");
      onSent && onSent();
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-8 soft-card p-6" data-testid="contact-support-form">
      <p className="text-xs uppercase tracking-wider text-propera font-semibold">Contact Support</p>
      <h2 className="text-xl font-bold text-neutral-900 mt-1">How can we help?</h2>
      <p className="text-sm text-neutral-500 mt-1">Send a message to our admin team. We reply by email and inside the app.</p>

      <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-6 mb-1.5">Subject</label>
      <input
        data-testid="support-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="e.g. Trouble verifying my email"
        className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm"
      />

      <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-4 mb-1.5">Category</label>
      <select
        data-testid="support-category"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm bg-white"
      >
        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>

      <label className="block text-xs uppercase tracking-wider text-neutral-500 font-semibold mt-4 mb-1.5">Message</label>
      <textarea
        data-testid="support-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Give us as much context as you can — steps you've tried, error messages, screenshots you can share on reply…"
        rows={6}
        className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm"
      />

      <button
        type="submit"
        data-testid="submit-support"
        disabled={submitting}
        className="w-full mt-5 bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold text-sm transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        {submitting ? "Sending…" : <>Send Request <Send size={14} /></>}
      </button>
    </form>
  );
}

function MyRequests() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets/mine");
      setTickets(data);
      if (data.length && !active) setActive(data[0]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function sendReply() {
    if (!active || reply.trim().length === 0) return;
    try {
      const { data } = await api.post(`/support/tickets/${active.id}/messages`, { body: reply.trim() });
      setActive(data);
      setReply("");
      await load();
    } catch {
      toast.error("Could not send reply");
    }
  }

  if (loading) return <div className="mt-8 text-center text-sm text-neutral-500">Loading…</div>;
  if (tickets.length === 0) {
    return (
      <div className="mt-8 soft-card p-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
          <Inbox size={22} />
        </div>
        <p className="text-lg font-semibold text-neutral-900">No requests yet</p>
        <p className="text-sm text-neutral-500 mt-1">Send us your first message from the Contact Support tab.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="soft-card p-2 max-h-[520px] overflow-y-auto space-y-1">
        {tickets.map((t) => (
          <button
            key={t.id}
            data-testid={`ticket-item-${t.id}`}
            onClick={() => setActive(t)}
            className={`w-full text-left px-3 py-3 rounded-xl transition ${active?.id === t.id ? "bg-propera-light" : "hover:bg-neutral-50"}`}
          >
            <div className="flex items-center gap-2 justify-between">
              <p className="text-sm font-semibold text-neutral-900 truncate flex-1">{t.subject}</p>
              <StatusPill status={t.status} />
            </div>
            <p className="text-xs text-neutral-500 truncate mt-0.5">{t.messages?.[t.messages.length - 1]?.body}</p>
            <p className="text-[10px] text-neutral-400 mt-0.5">{relativeTime(t.updated_at)}</p>
          </button>
        ))}
      </div>

      <div className="md:col-span-2 soft-card p-5 flex flex-col max-h-[520px]">
        {active ? (
          <>
            <div className="flex items-center justify-between gap-2 border-b border-neutral-100 pb-4">
              <div className="min-w-0">
                <p className="font-semibold text-neutral-900 truncate">{active.subject}</p>
                <p className="text-xs text-neutral-500 mt-0.5 capitalize">{active.category}</p>
              </div>
              <StatusPill status={active.status} />
            </div>
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {(active.messages || []).map((m) => {
                if (m.author_role === "system") {
                  return (
                    <div key={m.id} className="flex justify-center">
                      <span data-testid={`ticket-system-${m.id}`} className="text-[11px] uppercase tracking-wider text-neutral-500 bg-neutral-100 rounded-full px-3 py-1">{m.body}</span>
                    </div>
                  );
                }
                const isMe = m.author_role === "user";
                return (
                  <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${isMe ? "bg-propera text-white rounded-br-md" : "bg-neutral-100 text-neutral-800 rounded-bl-md"}`}>
                      {!isMe && <p className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-propera">Support</p>}
                      {m.body}
                      <p className={`text-[10px] mt-1 ${isMe ? "text-white/70" : "text-neutral-500"}`}>{relativeTime(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
              <input
                data-testid="ticket-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Reply…"
                className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm"
              />
              <button data-testid="ticket-reply-send" onClick={sendReply} className="bg-propera hover:bg-propera-hover text-white p-3 rounded-full">
                <Send size={16} />
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-neutral-500 text-center flex-1 flex items-center justify-center">Select a request</p>
        )}
      </div>
    </div>
  );
}

function Tab({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`px-5 py-3 text-sm font-semibold transition border-b-2 whitespace-nowrap ${active ? "text-propera border-propera" : "text-neutral-500 border-transparent hover:text-neutral-900"}`}>
      {children}
    </button>
  );
}
